import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import * as SockJS from 'sockjs-client';
import { v4 as uuidv4 } from 'uuid';
import { UserService } from '.';
import { OrganizationService } from './organization.service';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private stompClient: Client;
  private subscription: StompSubscription | null = null;
  private notificationSubscription: StompSubscription | null = null;

  public status: BehaviorSubject<WebSocketStatus> =
    new BehaviorSubject<WebSocketStatus>(WebSocketStatus.DISCONNECTED);

  public webSocketMessage: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);
  public chatMessage: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public notificationMessage: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  public websocketUrl: string = environment.websocketUrl;

  constructor(
    private userService: UserService,
    private http: HttpClient,
    private organizationService: OrganizationService
  ) {
    if (!localStorage.getItem('userId')) {
      localStorage.setItem('userId', uuidv4());
    }
  }

  public initialize(): void {
    if (this.stompClient && this.stompClient.connected) {
      console.log(
        'WebSocket: Disconnecting existing connection before reinitializing'
      );
      this.disconnect();
    }

    if (
      this.userService.jwtToken &&
      this.organizationService.currentOrganization?.alias &&
      this.userService.userId
    ) {
      this.connect(this.userService.userId, message => {
        this.webSocketMessage.next(message);
      });
    } else {
      console.warn(
        'WebSocket: Cannot initialize - missing required credentials',
        {
          hasToken: !!this.userService.jwtToken,
          hasOrganization:
            !!this.organizationService.currentOrganization?.alias,
          hasUserId: !!this.userService.userId,
        }
      );
      this.status.next(WebSocketStatus.DISCONNECTED);
    }
  }

  public ensureConnection(): Promise<boolean> {
    return new Promise(resolve => {
      if (this.status.value === WebSocketStatus.CONNECTED) {
        resolve(true);
        return;
      }

      if (this.status.value === WebSocketStatus.DISCONNECTED) {
        this.initialize();
      }

      const timeout = setTimeout(() => {
        resolve(false);
      }, 5000); // 5 second timeout

      const subscription = this.status.subscribe(status => {
        if (status === WebSocketStatus.CONNECTED) {
          clearTimeout(timeout);
          subscription.unsubscribe();
          resolve(true);
        } else if (status === WebSocketStatus.DISCONNECTED) {
        }
      });
    });
  }

  connect(userId: string, onMessage: (msg: string) => void): void {
    if (
      !this.userService.jwtToken ||
      !this.organizationService.currentOrganization?.alias ||
      !userId
    ) {
      console.warn('WebSocket: Missing required credentials for connection', {
        hasToken: !!this.userService.jwtToken,
        hasOrganization: !!this.organizationService.currentOrganization?.alias,
        hasUserId: !!userId,
      });
      this.status.next(WebSocketStatus.DISCONNECTED);
      return;
    }

    this.status.next(WebSocketStatus.CONNECTING);

    this.stompClient = new Client({
      webSocketFactory: () =>
        new SockJS(
          `${this.websocketUrl}/ws?token=${this.userService.jwtToken}&sid=${userId}`
        ),
      connectHeaders: {
        Authorization: 'Bearer ' + this.userService.jwtToken,
        organizationAlias: this.organizationService.currentOrganization.alias,
        userId: userId,
      },
      onConnect: () => {
        console.log('WebSocket Connected');
        this.status.next(WebSocketStatus.CONNECTED);

        this.subscription = this.stompClient.subscribe(
          `/topic/${this.organizationService.currentOrganization.alias}/${userId}`,
          (message: IMessage) => {
            onMessage(message.body);
            console.log('Message incoming: ', message.body);

            try {
              const parsedMessage = JSON.parse(message.body);
              if (parsedMessage.type === 'CHAT') {
                this.chatMessage.next(parsedMessage);
              }
            } catch (e) {}
          }
        );

        // Cancel previous notification subscription if exists
        if (this.notificationSubscription) {
          this.notificationSubscription.unsubscribe();
          this.notificationSubscription = null;
        }

        // Subscribe to notifications topic
        this.notificationSubscription = this.stompClient.subscribe(
          `/topic/${this.organizationService.currentOrganization.alias}/${userId}/insighthub/notifications`,
          (message: IMessage) => {
            console.log('Notification incoming: ', message.body);
            try {
              const parsedMessage = JSON.parse(message.body);
              this.notificationMessage.next(parsedMessage);
            } catch (e) {
              console.error('Error parsing notification message:', e);
            }
          }
        );
      },
      onStompError: frame => {
        console.error('STOMP error:', frame);
        this.status.next(WebSocketStatus.DISCONNECTED);
      },
      onWebSocketClose: event => {
        console.warn('WebSocket closed:', event);
        this.status.next(WebSocketStatus.DISCONNECTED);
      },
      onWebSocketError: event => {
        console.error('WebSocket error:', event);
        this.status.next(WebSocketStatus.DISCONNECTED);
      },
      debug: function (str) {
        // console.log('STOMP Debug:', str);
      },
      reconnectDelay: 30000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    this.stompClient.activate();
  }

  disconnect(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    if (this.stompClient) {
      this.stompClient.deactivate();
    }
    this.status.next(WebSocketStatus.DISCONNECTED);
  }

  public isConnected(): boolean {
    return this.status.value === WebSocketStatus.CONNECTED;
  }

  public forceReconnect(): void {
    this.disconnect();
    setTimeout(() => {
      this.initialize();
    }, 1000);
  }

  sendMessage(userId: string, message: string): Observable<any> {
    let headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${this.userService.jwtToken}`
    );

    return this.http.post(
      `${environment.websocketUrl}/api/v1/messages/send`,
      {
        userId: userId,
        message: message,
      },
      { headers: headers, responseType: 'text' }
    );
  }

  public sendMessageToTopic(topic: string, message: string): Observable<any> {
    let headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${this.userService.jwtToken}`
    );

    return this.http.post(
      `${environment.websocketUrl}/api/v1/messages/topics`,
      {
        topic: topic,
        message: message,
      },
      { headers: headers, responseType: 'text' }
    );
  }
}

export enum WebSocketStatus {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
}
